import { useState, useEffect, useRef } from "react";
import {
  Dropdown, Button, Avatar, Drawer, Typography, Switch, Menu, Modal, Space, Divider, Badge,
} from "antd";
import {
  SettingOutlined, LogoutOutlined, UserOutlined, MenuOutlined,
  ClockCircleOutlined, CalendarOutlined,
  DashboardOutlined, PlusCircleOutlined, TeamOutlined, SwapOutlined,
  AppstoreOutlined,
  SafetyOutlined, BuildOutlined, MessageOutlined, ShopOutlined,
  CustomerServiceOutlined, GlobalOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { baseUrl } from "../../config";
import { NavLink, Link, useHistory } from "react-router-dom";
import styled from "styled-components";
import { hasPermission } from "../permissions";

const { confirm } = Modal;
const { Title, Text } = Typography;

// ─── Navigation item config ───────────────────────────────────────────────────
// Multi-group items use `groups` (array of activeGroup keys) + `perms` (array of permission checks).
// Single items use `key` + `perm` + `permType` as before.
const TOP_NAV_ITEMS = [
  { key: 'dashboard',      label: 'Dashboard',   icon: DashboardOutlined,  path: '/dashboard',                 perm: '/dashboard',                 permType: 'readPermission'   },
  { key: 'add-payment',    label: 'Add Payment', icon: PlusCircleOutlined, path: '/payments/add_user_payment', perm: '/payments/add_user_payment', permType: 'createPermission' },
  { key: 'members',        label: 'Members',     icon: TeamOutlined,       path: '/users',                     perm: '/users',                     permType: 'readPermission'   },
  { key: 'transaction',    label: 'Transaction', icon: SwapOutlined,       path: '/payments',                  perm: '/payments',                  permType: 'readPermission'   },
  // ── Reports + Member Services + Work & Comm + Forms → "Reports" ──
  {
    key:    'operations',
    label:  'Reports',
    icon:   BuildOutlined,
    path:   '/reports/manual-report',
    groups: ['reports', 'member-services', 'work-comm', 'forms'],
    perms:  [
      { perm: '/reports/manual-report', permType: 'readPermission' },
      { perm: '/vishesh-sahyogi',        permType: 'readPermission' },
      { perm: '/inquiry',                permType: 'readPermission' },
      { perm: '/registration',           permType: 'readPermission' },
    ],
  },

  // ── Community Portal + Community Services → "Community" ──
  {
    key:    'community',
    label:  'Community',
    icon:   GlobalOutlined,
    path:   '/portal-updates',
    groups: ['community'],
    perms:  [
      { perm: '/portal-updates',      permType: 'readPermission' },
      { perm: '/admin-notifications',  permType: 'readPermission' },
      { perm: '/jobs-admin',           permType: 'readPermission' },
      { perm: '/vivah-admin',          permType: 'readPermission' },
      { perm: '/student-support',      permType: 'readPermission' },
      { perm: '/inquiry',              permType: 'readPermission' },
    ],
  },

  // ── Financial + System → "Manage" ──
  {
    key:    'administration',
    label:  'Manage',
    icon:   SafetyOutlined,
    path:   '/financial-reports',
    groups: ['financial', 'administration'],
    perms:  [
      { perm: '/expenditures', permType: 'readPermission' },
      { perm: '/role',         permType: 'readPermission' },
    ],
  },
  {
    key:        'data-directory',
    label:      'Directory',
    icon:       AppstoreOutlined,
    path:       '/pop-dashboard',
    alwaysShow: true,
  },
  { key: 'staff-chat',         label: 'Chat',       icon: MessageOutlined,  path: '/staff-chat',                    alwaysShow: true },
];

// ─── Styled components ────────────────────────────────────────────────────────

const HeaderWrapper = styled.div`
  background: #7a0000;
  height: 68px;
  padding: 0 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
  border-bottom: 1px solid rgba(255,255,255,0.08);
  display: flex;
  align-items: center;
  gap: 0;
  user-select: none;
`;

/* Brand logo */
const Brand = styled(NavLink)`
  font-size: 20px;
  font-weight: 900;
  color: #ffffff;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
  letter-spacing: -0.5px;
  padding-right: 16px;
  border-right: 1.5px solid rgba(255,255,255,0.2);

  &:hover { color: rgba(255,255,255,0.8); text-decoration: none; }
`;

/* Horizontal nav strip */
const TopNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 1;
  min-width: 0;
  padding: 0 12px;
  overflow-x: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }

  @media (max-width: 900px) { display: none; }
`;

/* Single nav pill */
const NavPill = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  color: rgba(255,255,255,0.75);
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;
  line-height: 1;

  .nav-icon {
    font-size: 15px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }

  &:hover {
    color: #ffffff;
    background: rgba(255,255,255,0.12);
    text-decoration: none;
  }

  /* Active state — pill with bottom indicator */
  &.nav-active {
    color: #ffffff;
    background: rgba(255,255,255,0.15);
    font-weight: 600;
    box-shadow: inset 0 -2.5px 0 #ffffff;
  }
`;

/* Right-side controls wrapper */
const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  padding-left: 12px;
  border-left: 1.5px solid rgba(255,255,255,0.2);
`;

/* Date / Time card */
const DateCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${p => p.$green
    ? 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)'
    : 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)'};
  color: #fff;
  padding: 7px 16px;
  border-radius: 10px;
  box-shadow: 0 2px 10px ${p => p.$green ? 'rgba(82,196,26,0.30)' : 'rgba(24,144,255,0.30)'};
  min-width: 108px;
  cursor: default;

  .lbl {
    font-size: 10px;
    font-weight: 600;
    opacity: 0.85;
    letter-spacing: 0.6px;
    display: flex;
    align-items: center;
    gap: 3px;
    line-height: 1.4;
  }
  .val {
    font-size: 15px;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: 0.3px;
  }

  @media (max-width: 1100px) { min-width: 90px; padding: 6px 12px; .val { font-size: 13px; } }
  @media (max-width: 700px)  { display: none; }
`;

/* Icon button (settings) */
const IconBtn = styled(Button)`
  width: 38px;
  height: 38px;
  min-width: 38px;
  border-radius: 9px;
  border: 1px solid rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: rgba(255,255,255,0.8);
  background: rgba(255,255,255,0.08);
  font-size: 16px;

  &:hover {
    color: #ffffff;
    border-color: rgba(255,255,255,0.4);
    background: rgba(255,255,255,0.15);
  }
`;

/* Profile button */
const ProfileBtn = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 14px 6px 8px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.2);
  background: rgba(255,255,255,0.08);
  cursor: pointer;
  transition: all 0.2s;
  min-width: 0;

  &:hover {
    border-color: rgba(255,255,255,0.4);
    background: rgba(255,255,255,0.15);
  }

  .pname {
    font-size: 13.5px;
    font-weight: 600;
    color: #ffffff;
    white-space: nowrap;
  }

  @media (max-width: 600px) {
    padding: 6px 8px;
    .pname { display: none; }
  }
`;

/* Hamburger (mobile only) */
const HamburgerBtn = styled(Button)`
  display: none;
  width: 38px;
  height: 38px;
  border-radius: 9px;
  padding: 0;
  flex-shrink: 0;
  margin-left: 8px;

  @media (max-width: 900px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

/* Settings color swatch */
const Swatch = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 2px solid transparent;
  background: ${p => p.$color};
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover {
    transform: scale(1.15);
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  }
  &.active-swatch {
    border-color: #fff;
    box-shadow: 0 0 0 2px ${p => p.$color};
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────
function Header({ activeGroup, onMobileMenuOpen, handleSidenavColor, handleSidenavType, handleFixedNavbar }) {
  const history = useHistory();
  const [settingsOpen,   setSettingsOpen]   = useState(false);
  const [sidenavType,    setSidenavType]    = useState('transparent');
  const [activeColor,    setActiveColor]    = useState('#1890ff');
  const [currentDate,    setCurrentDate]    = useState('');
  const [currentTime,    setCurrentTime]    = useState('');
  const [pendingInquiries, setPendingInquiries] = useState(0);
  const pollRef = useRef(null);

  useEffect(() => {
    const update = () => {
      const now = new Date();
      setCurrentDate(now.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }));
      setCurrentTime(now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, []);

  // Poll pending inquiry count every 60 seconds
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) return;
        const res = await axios.get(`${baseUrl}/inquiry/pending-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.status) setPendingInquiries(res.data.count || 0);
      } catch {}
    };
    fetchCount();
    pollRef.current = setInterval(fetchCount, 60000);
    return () => clearInterval(pollRef.current);
  }, []);

  const handleLogout = () => {
    confirm({
      title:      'Confirm Logout',
      content:    'Are you sure you want to logout?',
      okText:     'Yes, Logout',
      okType:     'danger',
      cancelText: 'Cancel',
      onOk() { localStorage.clear(); history.push('/sign-in'); },
    });
  };

  const pickColor = (color) => {
    setActiveColor(color);
    handleSidenavColor(color);
  };

  const profileMenu = (
    <Menu style={{ minWidth: 200, padding: '4px 0' }}>
      <Menu.Item key="info" disabled style={{ cursor: 'default' }}>
        <div style={{ padding: '6px 0' }}>
          <div style={{ fontWeight: 700, color: '#1a1a2e', fontSize: 14 }}>
            {localStorage.getItem('firstName')} {localStorage.getItem('lastName')}
          </div>
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
            {localStorage.getItem('email') || ''}
          </div>
        </div>
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="profile" icon={<UserOutlined />}>
        <Link to="/profile">Update Profile</Link>
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout} danger>
        Logout
      </Menu.Item>
    </Menu>
  );

  const visibleNavItems = TOP_NAV_ITEMS.filter(item => {
    if (item.alwaysShow) return true;
    try {
      if (item.perms) return item.perms.some(p => hasPermission(p.perm, p.permType));
      return hasPermission(item.perm, item.permType);
    } catch { return true; }
  });

  const COLOR_SWATCHES = [
    { color: '#1890ff', label: 'Blue'   },
    { color: '#52c41a', label: 'Green'  },
    { color: '#d9363e', label: 'Red'    },
    { color: '#fa8c16', label: 'Orange' },
    { color: '#722ed1', label: 'Purple' },
    { color: '#111111', label: 'Dark'   },
  ];

  return (
    <HeaderWrapper>

      {/* ── Brand ─────────────────────────────────────────────── */}
      <Brand to="/dashboard">ABRSVS</Brand>

      {/* ── Horizontal nav (desktop) ──────────────────────────── */}
      <TopNav>
        {visibleNavItems.map((item) => (
          <NavPill
            key={item.key}
            to={item.path}
            className={
              (item.groups ? item.groups.includes(activeGroup) : activeGroup === item.key)
                ? 'nav-active' : ''
            }
          >
            <span className="nav-icon"><item.icon /></span>
            {item.label}
          </NavPill>
        ))}
      </TopNav>

      {/* ── Hamburger (mobile) ───────────────────────────────── */}
      <HamburgerBtn icon={<MenuOutlined />} onClick={onMobileMenuOpen} />

      {/* ── Right controls ───────────────────────────────────── */}
      <RightSection>
        <DateCard>
          <div className="lbl"><CalendarOutlined />DATE</div>
          <div className="val">{currentDate}</div>
        </DateCard>

        <DateCard $green>
          <div className="lbl"><ClockCircleOutlined />TIME</div>
          <div className="val">{currentTime}</div>
        </DateCard>

        {/* ── Inquiry notification bell ── */}
        <Badge count={pendingInquiries} overflowCount={99} size="small" offset={[-4, 4]}>
          <button
            title={`${pendingInquiries} pending inquiries`}
            onClick={() => { setPendingInquiries(0); history.push('/inquiry'); }}
            style={{
              background: pendingInquiries > 0 ? 'rgba(255,59,48,0.15)' : 'rgba(255,255,255,0.08)',
              border: `1.5px solid ${pendingInquiries > 0 ? 'rgba(255,59,48,0.5)' : 'rgba(255,255,255,0.15)'}`,
              borderRadius: 10, width: 38, height: 38, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s',
            }}
          >
            <CustomerServiceOutlined style={{ fontSize: 18, color: pendingInquiries > 0 ? '#ff6b6b' : 'rgba(255,255,255,0.75)' }} />
          </button>
        </Badge>

        <Dropdown overlay={profileMenu} trigger={['click']} placement="bottomRight">
          <ProfileBtn>
            {localStorage.getItem('photo')
              ? <Avatar size={30} src={localStorage.getItem('photo')} />
              : <Avatar size={30} icon={<UserOutlined />} style={{ background: '#1890ff', flexShrink: 0 }} />
            }
            <span className="pname">{localStorage.getItem('firstName') || 'User'}</span>
          </ProfileBtn>
        </Dropdown>
      </RightSection>

      {/* ── Settings Drawer ───────────────────────────────────── */}
      <Drawer
        title={
          <div>
            <Title level={4} style={{ margin: 0 }}>Configurator</Title>
            <Text type="secondary" style={{ fontSize: 13 }}>Customize your dashboard</Text>
          </div>
        }
        placement="right"
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        width={320}
      >
        <div>
          <Title level={5}>Accent Color</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 14 }}>
            Choose your preferred sidebar accent
          </Text>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {COLOR_SWATCHES.map(({ color, label }) => (
              <Swatch
                key={color}
                $color={color}
                title={label}
                className={activeColor === color ? 'active-swatch' : ''}
                onClick={() => pickColor(color)}
              />
            ))}
          </div>

          <Divider />

          <Title level={5}>Sidebar Background</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Choose sidebar style
          </Text>
          <Space>
            <Button
              type={sidenavType === 'transparent' ? 'primary' : 'default'}
              onClick={() => { handleSidenavType('transparent'); setSidenavType('transparent'); }}
            >
              Transparent
            </Button>
            <Button
              type={sidenavType === 'white' ? 'primary' : 'default'}
              onClick={() => { handleSidenavType('#fff'); setSidenavType('white'); }}
            >
              White
            </Button>
          </Space>

          <Divider />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Title level={5} style={{ margin: 0 }}>Fixed Navbar</Title>
              <Text type="secondary" style={{ fontSize: 13 }}>Keep navbar pinned to top</Text>
            </div>
            <Switch defaultChecked onChange={handleFixedNavbar} />
          </div>
        </div>
      </Drawer>

    </HeaderWrapper>
  );
}

export default Header;
